import { db } from "@/lib/db";
import { loadUtils } from "@/lib/drawing-utils";
import { createMapSessionCleanup, formatMapCoordinates, type MapSession } from '@/lib/map-state';
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Viewpoint from "@arcgis/core/Viewpoint";
import FeatureLayerView from "@arcgis/core/views/layers/FeatureLayerView";
import MapView from "@arcgis/core/views/MapView";
import WebMap from "@arcgis/core/WebMap";
import LegendVM from "@arcgis/core/widgets/Legend/LegendViewModel";
import Track from "@arcgis/core/widgets/Track.js";
import { useLiveQuery } from "dexie-react-hooks";
import { useCallback, useEffect, useRef, useState, type RefObject } from "react";

/** Only show the loading indicator for loads longer than this (ms), so quick pans don't flicker. */
const DATA_LOADING_DELAY = 300;

interface MapSessionOptions {
    webMap: WebMap | null;
    polygonLayer: FeatureLayer | null;
    pointLayer: FeatureLayer | null;
    /** Changing this tears down and rebuilds the view. */
    retryAttempt: number;
    onNotice: (message: string) => void;
    /** Runs before the view is destroyed, while its layer views still exist. */
    beforeDispose: () => void;
}

/**
 * Owns one MapView and its SDK resources (tracking, legend, saved-item graphics layer).
 * The view is only exposed once the parcel layer view is ready, so a non-null `view`
 * means the map can be drawn on and queried.
 */
export function useMapSession(containerRef: RefObject<HTMLDivElement>, { webMap, polygonLayer, pointLayer, retryAttempt, onNotice, beforeDispose }: MapSessionOptions) {
    const [view, setView] = useState<MapView | null>(null);
    const [polygonLayerView, setPolygonLayerView] = useState<FeatureLayerView | null>(null);
    const [legend, setLegend] = useState<LegendVM | null>(null);
    const [graphicsLayer, setGraphicsLayer] = useState<GraphicsLayer | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [coords, setCoords] = useState('');
    const [rotation, setRotation] = useState(0);
    const [dataLoading, setDataLoading] = useState(false);
    const [isTracking, setIsTracking] = useState(false);
    const trackRef = useRef<Track | null>(null);
    const autoTracked = useRef(false);
    const allowLocation = useLiveQuery(async () => (await db.userSettings.where('name').equals('allowLocation').first())?.value as boolean | null);

    useEffect(() => {
        setView(null);
        setPolygonLayerView(null);
        setLegend(null);
        setGraphicsLayer(null);
        const container = containerRef.current;
        if (!container || !webMap || !polygonLayer || !pointLayer) return;
        let active = true;
        let cleanup: (() => void) | undefined;
        const controller = new AbortController();
        setLoading(true);
        setError(null);
        setDataLoading(false);
        const initialize = async () => {
            const [, viewPoint] = await Promise.all([
                loadUtils(),
                db.userSettings.where('name').equals('mapViewPoint').first(),
            ]);
            if (!active) return;
            const savedGraphics = new GraphicsLayer();
            webMap.add(savedGraphics);
            let mapView: MapView;
            try {
                mapView = new MapView({
                    map: webMap,
                    container,
                    constraints: { maxScale: 0, rotationEnabled: true, snapToZoom: false },
                    ui: { components: [] },
                    ...(viewPoint?.value
                        ? { viewpoint: Viewpoint.fromJSON(viewPoint.value) }
                        : { center: [-121.492, 38.579], scale: 18000 }),
                });
            } catch (error) {
                webMap.remove(savedGraphics);
                savedGraphics.destroy();
                throw error;
            }
            const session: MapSession = {
                view: mapView, handles: [],
                removeGraphics: () => { webMap.remove(savedGraphics); savedGraphics.destroy(); },
            };
            const dispose = createMapSessionCleanup(session);
            cleanup = () => {
                beforeDispose();
                dispose();
                if (trackRef.current === session.track) trackRef.current = null;
            };
            const track = new Track({
                view: mapView,
                goToLocationEnabled: true,
                // Follow the user without changing the zoom level they chose.
                goToOverride: (view, options) => {
                    options.target.zoom = view.zoom;
                    return view.goTo(options.target, options.options);
                },
                rotationEnabled: false,
                geolocationOptions: {
                    enableHighAccuracy: true,
                    timeout: 15000, // generous, to allow for location retrieval in areas with poor signal
                    maximumAge: 0
                },
            });
            trackRef.current = track;
            session.track = track;
            const legendViewModel = new LegendVM({ view: mapView, respectLayerVisibility: false });
            session.legend = legendViewModel;
            let dataLoadingTimer: number | undefined;
            session.handles.push(
                { remove: () => window.clearTimeout(dataLoadingTimer) },
                // True while the view fetches tiles, features or layers.
                reactiveUtils.watch(() => mapView.updating, (updating) => {
                    window.clearTimeout(dataLoadingTimer);
                    if (updating) dataLoadingTimer = window.setTimeout(() => setDataLoading(true), DATA_LOADING_DELAY);
                    else setDataLoading(false);
                }, { initial: true }),
                mapView.on('drag', () => { track.stop(); setIsTracking(false); }),
                track.on('track-error', () => { setIsTracking(false); onNotice('Your location is unavailable. Check location permissions and try again.'); }),
                reactiveUtils.watch(() => mapView.center, (center) => setCoords(formatMapCoordinates(center?.latitude, center?.longitude)), { initial: true }),
                reactiveUtils.watch(() => mapView.rotation, (value) => setRotation(value ?? 0), { initial: true }),
                reactiveUtils.watch(() => track.tracking, (tracking) => setIsTracking(tracking), { initial: true }),
                reactiveUtils.watch(() => mapView.stationary, (stationary) => {
                    if (!stationary || !mapView.viewpoint) return;
                    void db.userSettings.where('name').equals('mapViewPoint').modify({ value: mapView.viewpoint.toJSON() }).catch(() => {
                        if (active) onNotice('Your map position could not be saved on this device.');
                    });
                }),
            );
            await mapView.when();
            await polygonLayer.load({ signal: controller.signal });
            const layerView = await mapView.whenLayerView(polygonLayer);
            if (!active) return;
            setView(mapView);
            setPolygonLayerView(layerView as FeatureLayerView);
            setLegend(legendViewModel);
            setGraphicsLayer(savedGraphics);
            setLoading(false);
        };
        void initialize().catch(() => {
            cleanup?.();
            if (!active) return;
            setLoading(false);
            setError('The map could not start. Check your connection and try again.');
        });
        return () => {
            active = false;
            controller.abort();
            cleanup?.();
        };
    }, [containerRef, webMap, polygonLayer, pointLayer, retryAttempt, onNotice, beforeDispose]);

    // Start following the user once, on the first ready view, if they allowed location.
    useEffect(() => {
        const track = trackRef.current;
        if (view && allowLocation && track && !autoTracked.current) {
            autoTracked.current = true;
            track.start();
        }
    }, [view, allowLocation]);

    const startTracking = useCallback(() => { trackRef.current?.start(); }, []);
    const stopTracking = useCallback(() => {
        trackRef.current?.stop();
        setIsTracking(false);
    }, []);

    return { view, polygonLayerView, legend, graphicsLayer, loading, error, coords, rotation, dataLoading, isTracking, startTracking, stopTracking };
}
